/**
 * Settings page - manage personal preferences
 */
import React, { useEffect, useState } from 'react';
import { Shield, Mail, Bell, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import dataService, { UserSettings } from '../services/data';
import { useAuthStore } from '../store/authStore';

const SettingsPage: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      if (!user?.email) {
        setIsLoading(false);
        return;
      }

      const result = await dataService.getSettings(user.email);
      if (result.error) {
        toast.error(result.error);
      } else {
        setSettings(result.data!);
      }
      setIsLoading(false);
    };

    loadSettings();
  }, [user?.email]);

  const handleProfileChange = (field: keyof UserSettings['profile'], value: string) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            profile: {
              ...prev.profile,
              [field]: value
            }
          }
        : prev
    );
  };

  const handleNotificationToggle = (field: keyof UserSettings['notifications']) => {
    setSettings((prev) =>
      prev
        ? {
            ...prev,
            notifications: {
              ...prev.notifications,
              [field]: !prev.notifications[field]
            }
          }
        : prev
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!settings) return;

    setIsSaving(true);
    const result = await dataService.updateSettings(settings);
    if (result.error) {
      toast.error(result.error);
    } else if (result.data) {
      setSettings(result.data);
      toast.success('Settings updated');
    }
    setIsSaving(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Workspace Settings</h1>
            <p className="text-gray-600 mt-1">Manage notifications, security, and account preferences.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
            <Loader2 className="w-5 h-5 mr-3 animate-spin" />
            Loading settings...
          </div>
        ) : !settings ? (
          <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-600">
            Unable to load settings. Sign in again to continue.
          </div>
        ) : (
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">Profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">First name</label>
                  <input
                    value={settings.profile.firstName}
                    onChange={(event) => handleProfileChange('firstName', event.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Last name</label>
                  <input
                    value={settings.profile.lastName}
                    onChange={(event) => handleProfileChange('lastName', event.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Company</label>
                  <input
                    value={settings.profile.company}
                    onChange={(event) => handleProfileChange('company', event.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                  <input
                    value={settings.profile.role}
                    onChange={(event) => handleProfileChange('role', event.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <Shield className="w-8 h-8 text-blue-600 mb-4" />
                <h2 className="text-lg font-semibold text-gray-900">Security</h2>
                <p className="text-sm text-gray-600 mt-2">
                  Multi-factor authentication and device management controls are coming soon to the production release.
                </p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Mail className="w-8 h-8 text-blue-600 mb-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Email alerts</h2>
                    <p className="text-sm text-gray-600">Stay in sync with the latest diligence outcomes.</p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-5 w-5 text-blue-600"
                    checked={settings.notifications.analysisReady}
                    onChange={() => handleNotificationToggle('analysisReady')}
                  />
                </div>
                <label className="flex items-center space-x-3 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-blue-600"
                    checked={settings.notifications.weeklyDigest}
                    onChange={() => handleNotificationToggle('weeklyDigest')}
                  />
                  <span>Weekly deal digest</span>
                </label>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Bell className="w-8 h-8 text-blue-600 mb-2" />
                    <h2 className="text-lg font-semibold text-gray-900">Product updates</h2>
                    <p className="text-sm text-gray-600">Hear about new features before they launch.</p>
                  </div>
                  <input
                    type="checkbox"
                    className="h-5 w-5 text-blue-600"
                    checked={settings.notifications.productUpdates}
                    onChange={() => handleNotificationToggle('productUpdates')}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isSaving}
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSaving ? 'Saving...' : 'Save preferences'}
              </button>
            </div>
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
